// Document / Layer の既定値。デフォルト定義はここ 1 箇所に置く。

import { nanoid } from 'nanoid'
import type { Adjustment, AdjustmentKind, AdjustmentLayer, Document, DocumentBackground, FillLayer, FillPaint, GroupLayer, LayerLock, LayerStyle, RasterLayer, ShapeGeometry, ShapeLayer, TextLayer, TextStyle, SmartLayer, SelectiveColorRange } from '@/types/document'
import { SELECTIVE_COLOR_RANGES } from '@/types/document'
import { MAT3_IDENTITY, type Rect } from '@/types/geometry'

export const DEFAULT_LOCK: LayerLock = { all: false, pixels: false, position: false, transparency: false }

export interface NewDocumentOptions {
  name?: string
  width: number
  height: number
  dpi?: number
  background?: DocumentBackground
}

export function createEmptyDocument(opts: NewDocumentOptions): Document {
  const now = Date.now()
  return {
    schemaVersion: 1,
    meta: {
      id: nanoid(10),
      name: opts.name ?? '名称未設定',
      createdAt: now,
      updatedAt: now,
      width: Math.round(opts.width),
      height: Math.round(opts.height),
      dpi: opts.dpi ?? 72,
      colorProfile: 'sRGB',
      background: opts.background ?? 'transparent'
    },
    layers: {},
    rootChildren: [],
    activeLayerIds: [],
    selection: { pixelId: null },
    channels: [],
    patterns: [],
    comps: [],
    guides: [],
    pixelGeneration: 0
  }
}

export function createRasterLayer(partial: Partial<RasterLayer> & { pixelId: string }): RasterLayer {
  const layer: RasterLayer = {
    id: partial.id ?? newLayerId(),
    kind: 'raster',
    name: partial.name ?? 'レイヤー',
    visible: partial.visible ?? true,
    opacity: partial.opacity ?? 1,
    fillOpacity: partial.fillOpacity ?? 1,
    blendMode: partial.blendMode ?? 'normal',
    lock: partial.lock ?? { ...DEFAULT_LOCK },
    clipToBelow: partial.clipToBelow ?? false,
    pixelId: partial.pixelId,
    offset: partial.offset ?? { x: 0, y: 0 }
  }
  if (partial.mask) layer.mask = partial.mask
  if (partial.labelColor !== undefined) layer.labelColor = partial.labelColor
  return layer
}

export function createGroupLayer(partial: Partial<GroupLayer> = {}): GroupLayer {
  return {
    id: partial.id ?? newLayerId(),
    kind: 'group',
    name: partial.name ?? 'グループ',
    visible: partial.visible ?? true,
    opacity: partial.opacity ?? 1,
    fillOpacity: partial.fillOpacity ?? 1,
    blendMode: partial.blendMode ?? 'pass-through',
    lock: partial.lock ?? { ...DEFAULT_LOCK },
    clipToBelow: partial.clipToBelow ?? false,
    children: partial.children ?? [],
    expanded: partial.expanded ?? true
  }
}

export const DEFAULT_LEVELS = { inBlack: 0, inWhite: 255, gamma: 1, outBlack: 0, outWhite: 255 }

/** 各調整の既定パラメータ (恒等になる値) */
export function defaultAdjustment(kind: AdjustmentKind): Adjustment {
  switch (kind) {
    case 'levels': return { kind, rgb: { ...DEFAULT_LEVELS } }
    case 'curves': return { kind, rgb: [[0, 0], [255, 255]] }
    case 'brightness-contrast': return { kind, brightness: 0, contrast: 0 }
    case 'exposure': return { kind, exposure: 0, offset: 0, gamma: 1 }
    case 'hue-saturation': return { kind, master: { hue: 0, saturation: 0, lightness: 0 }, colorize: false, colorizeHue: 0, colorizeSaturation: 25, colorizeLightness: 0 }
    case 'color-balance': return { kind, shadows: [0, 0, 0], midtones: [0, 0, 0], highlights: [0, 0, 0], preserveLuminosity: true }
    case 'black-white': return { kind, reds: 40, yellows: 60, greens: 40, cyans: 60, blues: 20, magentas: 80, tint: false, tintHex: '#e6c896' }
    case 'photo-filter': return { kind, hex: '#ec8a00', density: 25, preserveLuminosity: true }
    case 'channel-mixer': return { kind, r: [100, 0, 0, 0], g: [0, 100, 0, 0], b: [0, 0, 100, 0], monochrome: false }
    case 'invert': return { kind }
    case 'posterize': return { kind, levels: 4 }
    case 'threshold': return { kind, level: 128 }
    case 'gradient-map': return { kind, stops: [{ pos: 0, hex: '#000000' }, { pos: 1, hex: '#ffffff' }], reverse: false }
    case 'vibrance': return { kind, vibrance: 0, saturation: 0 }
    case 'color-lookup': return { kind, lutPixelId: null, size: 2, name: '' }
    case 'selective-color': return { kind, ranges: Object.fromEntries(SELECTIVE_COLOR_RANGES.map(r => [r, [0, 0, 0, 0]])) as Record<SelectiveColorRange, [number, number, number, number]>, relative: true }
  }
}

export const ADJUSTMENT_KINDS: AdjustmentKind[] = ['brightness-contrast', 'levels', 'curves', 'exposure', 'vibrance', 'hue-saturation', 'color-balance', 'black-white', 'photo-filter', 'channel-mixer', 'invert', 'posterize', 'threshold', 'gradient-map', 'selective-color', 'color-lookup']

export function createAdjustmentLayer(partial: Partial<AdjustmentLayer> & { adjustment: Adjustment }): AdjustmentLayer {
  const layer: AdjustmentLayer = {
    id: partial.id ?? newLayerId(),
    kind: 'adjustment',
    name: partial.name ?? '調整レイヤー',
    visible: partial.visible ?? true,
    opacity: partial.opacity ?? 1,
    fillOpacity: partial.fillOpacity ?? 1,
    blendMode: partial.blendMode ?? 'normal',
    lock: partial.lock ?? { ...DEFAULT_LOCK },
    clipToBelow: partial.clipToBelow ?? false,
    adjustment: partial.adjustment
  }
  if (partial.mask) layer.mask = partial.mask
  return layer
}

export function createFillLayer(partial: Partial<FillLayer> & { fill: FillPaint }): FillLayer {
  const layer: FillLayer = {
    id: partial.id ?? newLayerId(),
    kind: 'fill',
    name: partial.name ?? '塗りつぶしレイヤー',
    visible: partial.visible ?? true,
    opacity: partial.opacity ?? 1,
    fillOpacity: partial.fillOpacity ?? 1,
    blendMode: partial.blendMode ?? 'normal',
    lock: partial.lock ?? { ...DEFAULT_LOCK },
    clipToBelow: partial.clipToBelow ?? false,
    fill: partial.fill
  }
  if (partial.mask) layer.mask = partial.mask
  return layer
}

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: 'Noto Sans JP',
  fontSize: 48,
  fontWeight: 400,
  italic: false,
  hex: '#000000',
  lineHeight: 1.2,
  letterSpacing: 0,
  align: 'left',
  orientation: 'horizontal'
}

export function createTextLayer(partial: Partial<Omit<TextLayer, 'style'>> & { position: { x: number; y: number }; style?: Partial<TextStyle> }): TextLayer {
  const layer: TextLayer = {
    id: partial.id ?? newLayerId(),
    kind: 'text',
    name: partial.name ?? ((partial.text ?? 'テキスト').split('\n')[0]!.slice(0, 30) || 'テキスト'),
    visible: partial.visible ?? true,
    opacity: partial.opacity ?? 1,
    fillOpacity: partial.fillOpacity ?? 1,
    blendMode: partial.blendMode ?? 'normal',
    lock: partial.lock ?? { ...DEFAULT_LOCK },
    clipToBelow: partial.clipToBelow ?? false,
    text: partial.text ?? '',
    style: { ...DEFAULT_TEXT_STYLE, ...(partial.style ?? {}) },
    position: partial.position
  }
  if (partial.boxWidth) layer.boxWidth = partial.boxWidth
  if (partial.mask) layer.mask = partial.mask
  return layer
}

/** 各効果の既定値 (Photoshop の初期値に近い) */
export function defaultStyle<K extends keyof LayerStyle>(key: K): NonNullable<LayerStyle[K]> {
  const shadow = { enabled: true, blendMode: 'multiply' as const, hex: '#000000', opacity: 0.75, angle: 120, distance: 5, spread: 0, size: 5 }
  switch (key) {
    case 'dropShadow': return { ...shadow } as unknown as NonNullable<LayerStyle[K]>
    case 'innerShadow': return { ...shadow } as unknown as NonNullable<LayerStyle[K]>
    case 'outerGlow': return { enabled: true, blendMode: 'screen', hex: '#ffffbe', opacity: 0.75, spread: 0, size: 5 } as unknown as NonNullable<LayerStyle[K]>
    case 'innerGlow': return { enabled: true, blendMode: 'screen', hex: '#ffffbe', opacity: 0.75, spread: 0, size: 5, source: 'edge' } as unknown as NonNullable<LayerStyle[K]>
    case 'bevel': return { enabled: true, style: 'inner', depth: 100, size: 5, soften: 0, angle: 120, altitude: 30, highlightHex: '#ffffff', highlightOpacity: 0.75, shadowHex: '#000000', shadowOpacity: 0.75 } as unknown as NonNullable<LayerStyle[K]>
    case 'satin': return { enabled: true, blendMode: 'multiply', hex: '#000000', opacity: 0.5, angle: 19, distance: 11, size: 14, invert: true } as unknown as NonNullable<LayerStyle[K]>
    case 'colorOverlay': return { enabled: true, blendMode: 'normal', opacity: 1, hex: '#ff0000' } as unknown as NonNullable<LayerStyle[K]>
    case 'gradientOverlay': return { enabled: true, blendMode: 'normal', opacity: 1, hex: '#000000', stops: [{ pos: 0, hex: '#000000' }, { pos: 1, hex: '#ffffff' }], angle: 90, style: 'linear' } as unknown as NonNullable<LayerStyle[K]>
    case 'patternOverlay': return { enabled: true, blendMode: 'normal', opacity: 1, patternId: '', scale: 1 } as unknown as NonNullable<LayerStyle[K]>
    case 'stroke': return { enabled: true, size: 3, position: 'outside', blendMode: 'normal', opacity: 1, hex: '#ff0000' } as unknown as NonNullable<LayerStyle[K]>
  }
  throw new Error('unknown style ' + String(key))
}

export const STYLE_KEYS: (keyof LayerStyle)[] = ['dropShadow', 'innerShadow', 'outerGlow', 'innerGlow', 'bevel', 'satin', 'colorOverlay', 'gradientOverlay', 'patternOverlay', 'stroke']

export function createSmartLayer(partial: Partial<SmartLayer> & { sourceId: string; sourceRect: Rect }): SmartLayer {
  const layer: SmartLayer = {
    id: partial.id ?? newLayerId(),
    kind: 'smart',
    name: partial.name ?? 'スマートオブジェクト',
    visible: partial.visible ?? true,
    opacity: partial.opacity ?? 1,
    fillOpacity: partial.fillOpacity ?? 1,
    blendMode: partial.blendMode ?? 'normal',
    lock: partial.lock ?? { ...DEFAULT_LOCK },
    clipToBelow: partial.clipToBelow ?? false,
    sourceId: partial.sourceId,
    sourceRect: { ...partial.sourceRect },
    transform: partial.transform ?? [...MAT3_IDENTITY],
    interpolation: partial.interpolation ?? 'bicubic'
  }
  if (partial.mask) layer.mask = partial.mask
  if (partial.styles) layer.styles = partial.styles
  return layer
}

export function createShapeLayer(partial: Partial<ShapeLayer> & { geometry: ShapeGeometry }): ShapeLayer {
  const names: Record<ShapeGeometry['type'], string> = { rect: '長方形', ellipse: '楕円', polygon: '多角形', line: 'ライン', path: 'シェイプ' }
  const layer: ShapeLayer = {
    id: partial.id ?? newLayerId(),
    kind: 'shape',
    name: partial.name ?? names[partial.geometry.type],
    visible: partial.visible ?? true,
    opacity: partial.opacity ?? 1,
    fillOpacity: partial.fillOpacity ?? 1,
    blendMode: partial.blendMode ?? 'normal',
    lock: partial.lock ?? { ...DEFAULT_LOCK },
    clipToBelow: partial.clipToBelow ?? false,
    geometry: partial.geometry,
    fillHex: partial.fillHex === undefined ? '#4f9cf5' : partial.fillHex,
    fillAlpha: partial.fillAlpha ?? 1,
    strokeHex: partial.strokeHex === undefined ? null : partial.strokeHex,
    strokeWidth: partial.strokeWidth ?? 2,
    strokeAlpha: partial.strokeAlpha ?? 1,
    strokeAlign: partial.strokeAlign ?? 'center',
    transform: partial.transform ?? [...MAT3_IDENTITY]
  }
  if (partial.mask) layer.mask = partial.mask
  if (partial.styles) layer.styles = partial.styles
  return layer
}

export function newPixelId(): string {
  return 'px_' + nanoid(10)
}

export function newLayerId(): string {
  return 'ly_' + nanoid(10)
}
